import { useEffect, useRef, useState, type ReactNode, type CSSProperties, type KeyboardEvent } from "react";
import { AlertTriangle, Archive, Box, Server as ServerIcon, Users } from "lucide-react";
import logoHorizontal from "../assets/brand/logo-horizontal.svg";
import logoMark from "../assets/brand/logo-mark.svg";
import logoStacked from "../assets/brand/logo-stacked.svg";
import emptyServers from "../assets/illustrations/empty-servers.svg";
import emptyWorlds from "../assets/illustrations/empty-worlds.svg";
import emptyBackups from "../assets/illustrations/empty-backups.svg";
import emptyMods from "../assets/illustrations/empty-mods.svg";
import errorState from "../assets/illustrations/error-state.svg";

export function Brand({ compact = false, stacked = false }: { compact?: boolean; stacked?: boolean }) {
  return <img className={`brand-logo ${compact ? "compact" : ""} ${stacked ? "stacked" : ""}`} src={compact ? logoMark : stacked ? logoStacked : logoHorizontal} alt="Reborn Valheim Control" />;
}

export type StatusTone = "online" | "offline" | "starting" | "stopping" | "installing" | "crashed" | "warning" | "success" | "failed" | "pending" | "unknown";

export function StatusBadge({ status, label }: { status: StatusTone; label?: string }) {
  const labels: Record<StatusTone, string> = {
    online: "Online", offline: "Desligado", starting: "Ligando", stopping: "Desligando",
    installing: "Preparando", crashed: "Falhou", warning: "Atenção", success: "Concluído",
    failed: "Falhou", pending: "Pendente", unknown: "Indisponível",
  };
  return <span className={`status-badge ${status}`}><i aria-hidden="true" />{label ?? labels[status]}</span>;
}

const emptyImages = { servers: emptyServers, worlds: emptyWorlds, backups: emptyBackups, mods: emptyMods, error: errorState };

export function EmptyState({ kind = "servers", title, description, action, compact = false }: {
  kind?: keyof typeof emptyImages;
  title: string;
  description?: string;
  action?: React.ReactNode;
  compact?: boolean;
}) {
  return <div className={`empty-state ${compact ? "compact" : ""}`}>
    <img src={emptyImages[kind]} alt="" aria-hidden="true" />
    <div><h3>{title}</h3>{description && <p>{description}</p>}{action}</div>
  </div>;
}

export function Avatar({ name, size = "md" }: { name: string; size?: "sm" | "md" | "lg" }) {
  const initials = name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "?";
  let hash = 0;
  for (const char of name) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return <span className={`ui-avatar ${size}`} style={{ "--avatar-hue": String(155 + hash % 70) } as CSSProperties} aria-label={`Avatar de ${name}`}>{initials}</span>;
}

export function ProgressBar({ value, label }: { value: number; label?: string }) {
  const safe = Math.max(0, Math.min(100, value));
  return <div className="ui-progress" aria-label={label} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(safe)}><i style={{ width: `${safe}%` }} /></div>;
}

export function PageSkeleton() {
  return <div className="page-skeleton" aria-label="Carregando conteúdo" aria-busy="true"><span /><div className="skeleton-grid"><i /><i /><i /></div><b /><b /><b /></div>;
}

export function CardSkeleton({ rows = 3 }: { rows?: number }) {
  return <div className="card-skeleton" aria-label="Carregando" aria-busy="true">{Array.from({ length: rows }, (_, index) => <div key={index}><i/><span><b/><small/></span></div>)}</div>;
}

export function MetricIcon({ kind }: { kind: "server" | "players" | "backup" | "alert" | "package" }) {
  const Icon = kind === "server" ? ServerIcon : kind === "players" ? Users : kind === "backup" ? Archive : kind === "alert" ? AlertTriangle : Box;
  return <Icon size={18} aria-hidden="true" />;
}

interface ActionDialogOptions {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "default" | "danger";
  input?: { label: string; defaultValue?: string; placeholder?: string; type?: string };
}

interface ActionDialogRequest extends ActionDialogOptions { resolve: (value: string | boolean | null) => void }

export function useActionDialog() {
  const [request, setRequest] = useState<ActionDialogRequest | null>(null);
  const confirm = (options: ActionDialogOptions) => new Promise<boolean>((resolve) => setRequest({ ...options, resolve: (value) => resolve(value === true) }));
  const prompt = (options: ActionDialogOptions & { input: NonNullable<ActionDialogOptions["input"]> }) => new Promise<string | null>((resolve) => setRequest({ ...options, resolve: (value) => resolve(typeof value === "string" ? value : null) }));
  const host: ReactNode = request ? <ActionDialog request={request} close={(value) => { request.resolve(value); setRequest(null); }}/> : null;
  return { confirm, prompt, host };
}

function ActionDialog({ request, close }: { request: ActionDialogRequest; close: (value: string | boolean | null) => void }) {
  const [value, setValue] = useState(request.input?.defaultValue ?? "");
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  useEffect(() => { (request.input ? inputRef.current : cancelRef.current)?.focus(); }, [request.input]);
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") close(request.input ? null : false);
    if (event.key !== "Tab") return;
    const controls = [...(dialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)') ?? [])];
    if (!controls.length) return;
    const first = controls[0]; const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  return <div className="dialog-layer" onKeyDown={handleKeyDown}>
    <div className="dialog-backdrop" aria-hidden="true" onClick={() => close(request.input ? null : false)}/>
    <section ref={dialogRef} className={`action-dialog ${request.tone === "danger" ? "danger-tone" : ""}`} role={request.tone === "danger" ? "alertdialog" : "dialog"} aria-modal="true" aria-labelledby="action-dialog-title" aria-describedby="action-dialog-description">
      <div className="dialog-mark" aria-hidden="true">ᛟ</div>
      <h2 id="action-dialog-title">{request.title}</h2>
      <p id="action-dialog-description">{request.description}</p>
      {request.input && <label className="field"><span>{request.input.label}</span><input ref={inputRef} type={request.input.type ?? "text"} value={value} placeholder={request.input.placeholder} onChange={(event) => setValue(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && value.trim()) close(value.trim()); }}/></label>}
      <div className="dialog-actions"><button ref={cancelRef} className="secondary" onClick={() => close(request.input ? null : false)}>{request.cancelLabel ?? "Cancelar"}</button><button className={request.tone === "danger" ? "danger" : "primary"} disabled={Boolean(request.input && !value.trim())} onClick={() => close(request.input ? value.trim() : true)}>{request.confirmLabel ?? "Confirmar"}</button></div>
    </section>
  </div>;
}
